import { MarkupElement, MarkupPolygonStyles } from './markup.model';

function createPattern(id: string, angle: number, maxSize: number) {
  const pattern = (document.getElementById(id) ??
    document.createElementNS(
      'http://www.w3.org/2000/svg',
      'pattern',
    )) as SVGPatternElement;

  pattern.id = id;
  pattern.setAttribute('x', '0');
  pattern.setAttribute('y', '0');
  pattern.setAttribute('width', maxSize.toString());
  pattern.setAttribute('height', maxSize.toString());
  pattern.setAttribute('patternUnits', 'userSpaceOnUse');
  pattern.setAttribute('patternContentUnits', 'userSpaceOnUse');
  pattern.setAttribute('patternTransform', `rotate(${angle})`);
  return pattern;
}

let defs: SVGDefsElement;

export function createSVG() {
  if (defs) {
    return defs;
  }

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');

  svg.setAttribute('width', '0');
  svg.setAttribute('height', '0');
  svg.style.position = 'absolute';

  defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
  svg.appendChild(defs);

  document.body.appendChild(svg);
  return defs;
}

export interface HatchOptions {
  id: MarkupElement['id'];
  style: MarkupPolygonStyles;
  interval: number;
  fill: string;
}

export function hatchStyle(options: HatchOptions) {
  const { style, interval, fill, id } = options;
  const maxSize = interval + 2;
  const hatchStroke = 2;

  let pattern: SVGPatternElement;
  const paths: SVGPathElement[] = [];
  switch (style) {
    case MarkupPolygonStyles.solid:
      return {
        fillColor: fill,
      };

    case MarkupPolygonStyles.horizontal:
      pattern = createPattern(id, 0, maxSize);
      paths.push(createHorizontalLine(fill, hatchStroke, maxSize));
      break;

    case MarkupPolygonStyles.vertical:
      pattern = createPattern(id, 90, maxSize);
      paths.push(createHorizontalLine(fill, hatchStroke, maxSize));
      break;

    case MarkupPolygonStyles.backward_diagonal:
      pattern = createPattern(id, 45, maxSize);
      paths.push(createHorizontalLine(fill, hatchStroke, maxSize));
      break;

    case MarkupPolygonStyles.forward_diagonal:
      pattern = createPattern(id, -45, maxSize);
      paths.push(createHorizontalLine(fill, hatchStroke, maxSize));
      break;

    case MarkupPolygonStyles.diagonal_cross:
      pattern = createPattern(id, 45, maxSize);
      paths.push(...createCrossHatch(fill, hatchStroke, maxSize));
      break;

    case MarkupPolygonStyles.cross:
      pattern = createPattern(id, 0, maxSize);
      paths.push(...createCrossHatch(fill, hatchStroke, maxSize));
      break;
  }

  while (pattern.firstChild) {
    pattern.removeChild(pattern.firstChild);
  }
  paths.forEach((path) => pattern.appendChild(path));

  const defs = createSVG();
  defs.appendChild(pattern);

  return {
    fillColor: `url(#${pattern.id})`,
  };
}

function createCrossHatch(fillColor: string, width: number, maxSize: number) {
  const paths: SVGPathElement[] = [];

  paths.push(
    createHorizontalLine(fillColor, width, maxSize),
    createVerticalLine(fillColor, width, maxSize),
  );

  return paths;
}

function createHorizontalLine(
  fillColor: string,
  width: number,
  maxSize: number,
) {
  const foreground = document.createElementNS(
    'http://www.w3.org/2000/svg',
    'path',
  );
  foreground.setAttribute('stroke', fillColor);
  foreground.setAttribute('stroke-width', width.toString());
  foreground.setAttribute('pointer-events', 'none');
  foreground.setAttribute('d', `M0 ${maxSize / 2}h${maxSize * 2}`);

  return foreground;
}

function createVerticalLine(fillColor: string, width: number, maxSize: number) {
  const foreground = document.createElementNS(
    'http://www.w3.org/2000/svg',
    'path',
  );
  foreground.setAttribute('stroke', fillColor);
  foreground.setAttribute('stroke-width', width.toString());
  foreground.setAttribute('pointer-events', 'none');

  const centerX = maxSize / 2;
  foreground.setAttribute('d', `M${centerX} 0V${maxSize * 2}`);

  return foreground;
}
